import { useEffect, useState } from 'react';
import { Heart, Trash2, ShoppingCart, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { useToast } from '@/hooks/use-toast';
import { useProducts } from '@/hooks/useProducts';

interface WishlistItem {
  id: string;
  product_id: string;
  product_name: string;
  product_image: string | null;
  product_price: number | null;
  notes: string | null;
  added_at: string;
}

interface WishlistManagerProps {
  variant?: 'full' | 'compact';
  showAddToWishlist?: boolean;
  productId?: string;
  productName?: string;
  productImage?: string;
  productPrice?: number;
}

export function WishlistManager({ 
  variant = 'full',
  showAddToWishlist = false,
  productId,
  productName,
  productImage,
  productPrice
}: WishlistManagerProps) {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const { data: products } = useProducts();
  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInWishlist, setIsInWishlist] = useState(false);

  useEffect(() => {
    if (user) {
      fetchWishlist();
    }
  }, [user]);

  useEffect(() => {
    if (productId && wishlistItems.length > 0) {
      setIsInWishlist(wishlistItems.some(item => item.product_id === productId));
    }
  }, [productId, wishlistItems]);

  const fetchWishlist = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('wishlists')
        .select('*')
        .eq('user_id', user.id)
        .order('added_at', { ascending: false });

      if (error) throw error;
      setWishlistItems(data || []);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToWishlist = async () => {
    if (!user || !productId || !productName) return;

    try {
      const { error } = await supabase
        .from('wishlists')
        .insert({
          user_id: user.id,
          product_id: productId,
          product_name: productName,
          product_image: productImage,
          product_price: productPrice,
        });

      if (error) throw error;
      
      await fetchWishlist();
      setIsInWishlist(true);
      toast({
        title: "Added to wishlist",
        description: `${productName} has been saved to your wishlist`,
      });
    } catch (error) {
      console.error('Failed to add to wishlist:', error);
      toast({
        title: "Error",
        description: "Failed to add item to wishlist",
        variant: "destructive",
      });
    }
  };

  const removeFromWishlist = async (itemId?: string, itemProductId?: string) => {
    if (!user) return;

    try {
      let query = supabase.from('wishlists').delete();
      
      if (itemId) {
        query = query.eq('id', itemId);
      } else if (itemProductId) {
        query = query.eq('user_id', user.id).eq('product_id', itemProductId);
      }

      const { error } = await query;
      if (error) throw error;
      
      await fetchWishlist();
      if (itemProductId === productId) {
        setIsInWishlist(false);
      }
      
      toast({
        title: "Removed from wishlist",
        description: "Item has been removed from your wishlist",
      });
    } catch (error) {
      console.error('Failed to remove from wishlist:', error);
      toast({
        title: "Error",
        description: "Failed to remove item from wishlist",
        variant: "destructive",
      });
    }
  };

  const addWishlistItemToCart = (item: WishlistItem) => {
    const product = products.find(p => p.id === item.product_id);
    if (product) {
      addToCart(product, product.packSizes[0].size, false, 'Original');
    }
    
    toast({
      title: "Added to cart",
      description: `${item.product_name} has been added to your cart`,
    });
  };

  // Compact variant for product pages - just the heart button
  if (variant === 'compact' && showAddToWishlist) {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={isInWishlist ? () => removeFromWishlist(undefined, productId) : addToWishlist}
        className="hover:bg-solr-nebula-purple/20"
        disabled={!user}
      >
        <Heart 
          className={`w-5 h-5 transition-colors ${
            isInWishlist 
              ? 'text-red-500 fill-red-500' 
              : 'text-solr-starfield-white/70 hover:text-red-500'
          }`} 
        />
      </Button>
    );
  }

  // Full wishlist manager
  return (
    <Card className="bg-solr-void-black/80 border-solr-nebula-purple backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-solr-starfield-white">
          <Heart className="w-5 h-5" />
          My Wishlist
          <Badge variant="secondary" className="ml-auto bg-solr-nebula-purple/20 text-solr-starfield-white">
            {wishlistItems.length} items
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-solr-space-dark/50">
                <div className="w-16 h-16 bg-solr-nebula-purple/20 rounded-lg animate-pulse"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-solr-nebula-purple/20 rounded animate-pulse"></div>
                  <div className="h-3 bg-solr-nebula-purple/10 rounded animate-pulse w-2/3"></div>
                </div>
              </div>
            ))}
          </div>
        ) : wishlistItems.length === 0 ? (
          <div className="text-center py-8">
            <Heart className="w-12 h-12 text-solr-starfield-white/30 mx-auto mb-4" />
            <p className="text-solr-starfield-white/70 mb-2">Your wishlist is empty</p>
            <p className="text-sm text-solr-starfield-white/50">
              Save products you love for later
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {wishlistItems.map((item) => (
              <div 
                key={item.id}
                className="flex items-center gap-4 p-3 rounded-lg bg-solr-space-dark/50 border border-solr-nebula-purple/20 hover:bg-solr-space-dark/70 transition-colors"
              >
                {item.product_image ? (
                  <img 
                    src={item.product_image} 
                    alt={item.product_name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-16 h-16 bg-solr-nebula-purple/20 rounded-lg flex items-center justify-center">
                    <Plus className="w-6 h-6 text-solr-starfield-white/50" />
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-solr-starfield-white truncate">
                    {item.product_name}
                  </h4>
                  {item.product_price && (
                    <p className="text-sm text-solr-starfield-white/70">
                      £{item.product_price.toFixed(2)}
                    </p>
                  )}
                  <p className="text-xs text-solr-starfield-white/50">
                    Added {new Date(item.added_at).toLocaleDateString()}
                  </p>
                  {item.notes && (
                    <p className="text-xs text-solr-starfield-white/60 mt-1 italic">
                      {item.notes}
                    </p>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => addWishlistItemToCart(item)}
                    className="bg-solr-nebula-purple hover:bg-solr-nebula-purple/80 text-white"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Add to Cart
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFromWishlist(item.id)}
                    className="hover:bg-red-500/20 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}